import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { ReactComponent as CloseIcon } from '../../icons/close-square.svg';
import { ReactComponent as TruckIcon } from '../../icons/user.svg';
import { CREATE_VENDOR_TRUCK } from '../../services/graphql/dropoffs/dropoffsMutations';
import FormButton from '../atoms/FormButton';
import Input from '../atoms/Inputs/Input';
import InputNumber from '../atoms/Inputs/InputNumber';

type TForm = {
  license_plate: string;
  tk: string;
  capacity: number;
};

const AddTruckModalForm = ({
  isOpen,
  setIsOpen,
  vendorId,
  onCreated,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  vendorId: string;
  onCreated: (id: string) => void;
}) => {
  const { control, reset, handleSubmit } = useForm<TForm>();
  const { t } = useTranslation();
  const [createTruck] = useMutation(CREATE_VENDOR_TRUCK);

  const onSubmit = (data: TForm) => {
    if (!vendorId) return;
    createTruck({
      variables: {
        vendor_id: vendorId,
        license_plate: data.license_plate,
        tk: data.tk,
        capacity: data.capacity,
      },
      onCompleted: (res) => {
        reset({});
        onCreated(res.createVendorTruck.data);
      },
    });
  };

  const closeModal = () => {
    reset();
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      appElement={document.getElementById('root') || undefined}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        content: {
          maxWidth: '470px',
          height: 'min-content',
          margin: 'auto',
          borderRadius: '20px',
        },
      }}
      contentLabel="Alert"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col justify-between gap-y-10 p-2"
      >
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-x-2">
            <TruckIcon />
            <span>{t('addTruckForm.title')}</span>
          </div>
          <div className="flex justify-end ">
            <button onClick={closeModal}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-y-6">
          <Input
            label={t('addTruckForm.license-plate')}
            control={control}
            defaultValue=""
            inputField="license_plate"
            type="text"
            validations={{
              required: t('global.required'),
            }}
          />
          <Input
            label={t('addTruckForm.tk')}
            control={control}
            defaultValue=""
            inputField="tk"
            type="text"
            validations={{
              required: t('global.required'),
            }}
          />
          <InputNumber
            label={t('addTruckForm.capacity')}
            control={control}
            defaultValue=""
            inputField="capacity"
            validations={{
              required: t('global.required'),
            }}
            suffix=" Gal"
            decimalCount={0}
          />
        </div>
        <div className="mb-2 flex flex-row justify-center gap-x-3">
          <FormButton
            id="cancelAddTruck"
            icon={''}
            label={t('addTruckForm.cancel')}
            color="white"
            textColor="text-ReyesGreen"
            type="button"
            reverse
            width="auto"
            onClick={closeModal}
          />
          <FormButton
            icon={''}
            type="submit"
            id="createTruck"
            label={t('addTruckForm.create')}
            color="ReyesGreen"
            reverse
            width="auto"
          />
        </div>
      </form>
    </Modal>
  );
};

export default AddTruckModalForm;
